import { useState } from 'react';
import { Shield, Send, CheckCircle, Lock, Camera, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { toast as sonnerToast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_insure-plus-1/artifacts/24wkpkvm_logo.png';

const PRIORITY_OPTIONS = [
  'Low or $0 monthly plan premium',
  'Keep my doctors and preferred hospital',
  'No or low medical deductible',
  'Low doctor visit copays',
  'Dental coverage',
  'Low coinsurance if I get sick',
  'Vision and hearing benefits',
  'OTC allowances and extra benefits',
  'Free gym membership',
  'Freedom to go to any doctor or hospital',
  'Fewer referrals and approvals',
];

const STEP_LABELS = [
  'Your Information',
  'Choose Your Priorities',
  'Rank Your Top 3',
];

const INITIAL_FORM = {
  full_name: '', date_of_birth: '', address: '', email: '', phone: '',
  employment_status: '', planned_retirement_date: '',
  current_insurance: '', monthly_premium: '', insurance_card_photo: '',
  has_medicaid: 'no', medicaid_number: '',
  enrolled_part_a: 'no', part_a_start_date: '',
  enrolled_part_b: 'no', part_b_start_date: '',
  medicare_number: '',
  medications: '', doctors: '', medical_conditions: '', preferred_hospital: '',
  selected_priorities: [],
  priority_rank_1: '', priority_rank_2: '', priority_rank_3: '',
  other_concerns: '',
};

/* ─── Progress Bar ─── */
const ProgressBar = ({ step }) => (
  <div className="mb-8" data-testid="progress-bar">
    <div className="flex justify-between mb-3">
      {STEP_LABELS.map((label, i) => (
        <div key={label} className={`flex-1 text-center ${i < STEP_LABELS.length - 1 ? 'mr-2' : ''}`}>
          <div className={`text-xs font-bold mb-1.5 ${i <= step ? 'text-blue-600' : 'text-gray-400'}`}>
            Step {i + 1} of 3
          </div>
          <div className={`h-2 rounded-full ${i <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />
          <div className={`text-xs mt-1.5 ${i <= step ? 'text-gray-700 font-medium' : 'text-gray-400'}`}>
            {label}
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Step 1: Existing Intake Fields ─── */
const StepIntake = ({ form, onChange, onPhotoUpload, photoPreview, photoName, removePhoto, onContinue }) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    onContinue();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield className="h-5 w-5 text-blue-600" />
            Personal Information
          </CardTitle>
          <CardDescription>Your basic contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <Input name="full_name" value={form.full_name} onChange={onChange} required placeholder="First and last name" data-testid="intake-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
            <Input type="date" name="date_of_birth" value={form.date_of_birth} onChange={onChange} required data-testid="intake-dob" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
            <Input name="address" value={form.address} onChange={onChange} required placeholder="Street, City, State, ZIP" data-testid="intake-address" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input type="email" name="email" value={form.email} onChange={onChange} required placeholder="you@example.com" data-testid="intake-email" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <Input type="tel" name="phone" value={form.phone} onChange={onChange} required placeholder="(555) 123-4567" data-testid="intake-phone" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
            <div className="flex flex-col space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="employment_status" value="full-time" checked={form.employment_status === 'full-time'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Full-time employed</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="employment_status" value="part-time" checked={form.employment_status === 'part-time'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Part-time employed</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="employment_status" value="retired" checked={form.employment_status === 'retired'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Retired / Not working</span>
              </label>
            </div>
          </div>
          {form.employment_status && form.employment_status !== 'retired' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Planned Retirement Date</label>
              <Input type="date" name="planned_retirement_date" value={form.planned_retirement_date} onChange={onChange} data-testid="intake-retirement-date" />
              <p className="text-xs text-gray-400 mt-1">When do you plan to retire? (approximate is fine)</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Insurance Information</CardTitle>
          <CardDescription>Your current coverage details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Insurance</label>
            <Input name="current_insurance" value={form.current_insurance} onChange={onChange} placeholder="e.g., Original Medicare, Aetna, Employer plan" data-testid="intake-insurance" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Premium</label>
            <Input name="monthly_premium" value={form.monthly_premium} onChange={onChange} placeholder="e.g., $150/month" data-testid="intake-premium" />
            <p className="text-xs text-gray-400 mt-1">How much do you currently pay per month for your insurance?</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Card Photo</label>
            {photoPreview ? (
              <div className="relative border-2 border-blue-200 rounded-xl p-3 bg-blue-50">
                <img src={photoPreview} alt="Insurance card" className="w-full max-h-48 object-contain rounded-lg" />
                <p className="text-xs text-gray-500 mt-2 text-center">{photoName}</p>
                <button type="button" onClick={removePhoto} className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">X</button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors" data-testid="intake-photo-upload">
                <Camera className="h-8 w-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500 font-medium">Tap to upload a photo of your insurance card</span>
                <span className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</span>
                <input type="file" accept="image/*" capture="environment" onChange={onPhotoUpload} className="hidden" />
              </label>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Do you receive Medicaid assistance?</label>
            <div className="flex space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="has_medicaid" value="yes" checked={form.has_medicaid === 'yes'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="has_medicaid" value="no" checked={form.has_medicaid === 'no'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">No</span>
              </label>
            </div>
          </div>
          {form.has_medicaid === 'yes' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medicaid Number</label>
              <Input name="medicaid_number" value={form.medicaid_number} onChange={onChange} placeholder="Enter your Medicaid ID number" data-testid="intake-medicaid-number" />
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Medicare Enrollment</CardTitle>
          <CardDescription>Your current Medicare enrollment status</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Are you enrolled in Medicare Part A?</label>
            <div className="flex space-x-6 mb-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="enrolled_part_a" value="yes" checked={form.enrolled_part_a === 'yes'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="enrolled_part_a" value="no" checked={form.enrolled_part_a === 'no'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">No</span>
              </label>
            </div>
            {form.enrolled_part_a === 'yes' && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Part A Effective Start Date</label>
                <Input type="date" name="part_a_start_date" value={form.part_a_start_date} onChange={onChange} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Are you enrolled in Medicare Part B?</label>
            <div className="flex space-x-6 mb-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="enrolled_part_b" value="yes" checked={form.enrolled_part_b === 'yes'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">Yes</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="radio" name="enrolled_part_b" value="no" checked={form.enrolled_part_b === 'no'} onChange={onChange} className="w-5 h-5 text-blue-600" />
                <span className="text-base text-gray-700">No</span>
              </label>
            </div>
            {form.enrolled_part_b === 'yes' && (
              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">Part B Effective Start Date</label>
                <Input type="date" name="part_b_start_date" value={form.part_b_start_date} onChange={onChange} />
              </div>
            )}
          </div>
          {(form.enrolled_part_a === 'yes' || form.enrolled_part_b === 'yes') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number (MBI)</label>
              <Input name="medicare_number" value={form.medicare_number} onChange={onChange} placeholder="e.g., 1EG4-TE5-MK72" />
              <p className="text-xs text-gray-400 mt-1">Found on your red, white, and blue Medicare card</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Medical Information</CardTitle>
          <CardDescription>This helps us understand your healthcare needs</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Medications</label>
            <Textarea name="medications" value={form.medications} onChange={onChange} placeholder="List your current medications, one per line" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Doctors & Specialists</label>
            <Textarea name="doctors" value={form.doctors} onChange={onChange} placeholder="List your doctors and specialists, one per line" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medical Conditions</label>
            <Textarea name="medical_conditions" value={form.medical_conditions} onChange={onChange} placeholder="List any current medical conditions" rows={3} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Hospital</label>
            <Input name="preferred_hospital" value={form.preferred_hospital} onChange={onChange} placeholder="e.g., Westchester Medical Center" />
          </div>
        </CardContent>
      </Card>

      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg font-semibold rounded-xl" data-testid="step1-continue">
        Continue to Medicare Priorities
        <ChevronRight className="ml-2 h-5 w-5" />
      </Button>
    </form>
  );
};

/* ─── Step 2: Choose 5 Priorities ─── */
const StepPriorities = ({ selected, onToggle, onBack, onContinue }) => {
  const count = selected.length;
  const canContinue = count === 5;

  const handleContinue = () => {
    if (!canContinue) {
      sonnerToast.error(`Please select exactly 5 priorities. You've selected ${count}.`);
      return;
    }
    onContinue();
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Your Medicare Priorities</h2>
        <p className="text-gray-500 text-base">Help us understand what matters most to you when choosing your Medicare coverage.</p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-center">
        <p className="text-blue-800 font-semibold text-base">Please choose the FIVE features that are most important to you.</p>
        <p className={`text-lg font-bold mt-2 ${canContinue ? 'text-green-600' : 'text-blue-600'}`} data-testid="priority-counter">
          {count} of 5 selected {canContinue && <CheckCircle className="inline h-5 w-5 ml-1" />}
        </p>
      </div>

      <div className="space-y-3 mb-8" data-testid="priority-list">
        {PRIORITY_OPTIONS.map((option) => {
          const isSelected = selected.includes(option);
          const isDisabled = !isSelected && count >= 5;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onToggle(option)}
              disabled={isDisabled}
              className={`w-full flex items-center gap-4 p-4 sm:p-5 rounded-xl border-2 text-left transition-all ${
                isSelected
                  ? 'border-blue-600 bg-blue-50 shadow-sm'
                  : isDisabled
                  ? 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 cursor-pointer'
              }`}
              data-testid={`priority-option-${PRIORITY_OPTIONS.indexOf(option)}`}
            >
              <div className={`w-7 h-7 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
              }`}>
                {isSelected && <CheckCircle className="h-5 w-5 text-white" />}
              </div>
              <span className={`text-base sm:text-lg ${isSelected ? 'text-blue-900 font-semibold' : 'text-gray-700'}`}>
                {option}
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex gap-4">
        <Button type="button" variant="outline" onClick={onBack} className="flex-1 py-6 text-base font-semibold rounded-xl border-2" data-testid="step2-back">
          <ChevronLeft className="mr-2 h-5 w-5" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleContinue}
          className={`flex-[2] py-6 text-lg font-semibold rounded-xl ${canContinue ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          data-testid="step2-continue"
        >
          Continue to Rank My Top 3
          <ChevronRight className="ml-2 h-5 w-5" />
        </Button>
      </div>
    </div>
  );
};

/* ─── Step 3: Rank Top 3 + Must-Keep ─── */
const StepRanking = ({ selected, rank1, rank2, rank3, mustKeep, onChangeRank, onChangeMustKeep, onBack, onSubmit, isSubmitting }) => {
  const getAvailableOptions = (currentRank) => {
    const taken = [rank1, rank2, rank3].filter((r) => r && r !== currentRank);
    return selected.filter((p) => !taken.includes(p));
  };

  const canSubmit = rank1 && rank2 && rank3;

  const handleSubmit = () => {
    if (!canSubmit) {
      sonnerToast.error('Please rank all three priorities before submitting.');
      return;
    }
    onSubmit();
  };

  return (
    <div>
      <div className="text-center mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Rank Your Top 3 Priorities</h2>
        <p className="text-gray-500 text-base">Of the five features you selected, which THREE matter most to you?</p>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6 space-y-6">
          {/* Rank 1 */}
          <div>
            <label className="block text-base font-semibold text-gray-900 mb-2">1. Most Important</label>
            <select
              value={rank1}
              onChange={(e) => onChangeRank('priority_rank_1', e.target.value)}
              className="w-full p-4 text-base border-2 border-gray-200 rounded-xl bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none"
              data-testid="rank-1"
            >
              <option value="">Select your #1 priority</option>
              {getAvailableOptions(rank1).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Rank 2 */}
          <div>
            <label className="block text-base font-semibold text-gray-900 mb-2">2. Second Most Important</label>
            <select
              value={rank2}
              onChange={(e) => onChangeRank('priority_rank_2', e.target.value)}
              className="w-full p-4 text-base border-2 border-gray-200 rounded-xl bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none"
              data-testid="rank-2"
            >
              <option value="">Select your #2 priority</option>
              {getAvailableOptions(rank2).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          {/* Rank 3 */}
          <div>
            <label className="block text-base font-semibold text-gray-900 mb-2">3. Third Most Important</label>
            <select
              value={rank3}
              onChange={(e) => onChangeRank('priority_rank_3', e.target.value)}
              className="w-full p-4 text-base border-2 border-gray-200 rounded-xl bg-white focus:border-blue-600 focus:ring-1 focus:ring-blue-600 outline-none"
              data-testid="rank-3"
            >
              <option value="">Select your #3 priority</option>
              {getAvailableOptions(rank3).map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Other Concerns */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Do you have any other questions or concerns about health insurance and retirement?</CardTitle>
          <CardDescription>For example, long-term care insurance, life insurance, final expense insurance, retirement income security, supplemental coverage, estate planning, prescription drug costs, or coverage for a spouse.</CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={mustKeep}
            onChange={(e) => onChangeMustKeep(e.target.value)}
            placeholder="Please share any other concerns or questions you have about your health insurance and retirement planning."
            rows={4}
            className="text-base"
            data-testid="other-concerns"
          />
          <p className="text-xs text-gray-400 mt-1">Optional</p>
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <Button type="button" variant="outline" onClick={onBack} className="flex-1 py-6 text-base font-semibold rounded-xl border-2" data-testid="step3-back">
          <ChevronLeft className="mr-2 h-5 w-5" />
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !canSubmit}
          className={`flex-[2] py-6 text-lg font-semibold rounded-xl ${canSubmit ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          data-testid="step3-submit"
        >
          <Send className="mr-2 h-5 w-5" />
          {isSubmitting ? 'Submitting Securely...' : 'Submit Information Securely'}
        </Button>
      </div>

      <p className="text-center text-xs text-gray-400 mt-4">
        Your information is encrypted and transmitted securely. By submitting this form, you consent to TruBridge Health using this information to assist you with your Medicare options.
      </p>
    </div>
  );
};

/* ─── Main Component ─── */
export default function MedicalIntakeForm() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoName, setPhotoName] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { sonnerToast.error('Photo must be under 5MB'); return; }
    setPhotoName(file.name);
    const reader = new FileReader();
    reader.onloadend = () => { setForm(prev => ({ ...prev, insurance_card_photo: reader.result })); setPhotoPreview(reader.result); };
    reader.readAsDataURL(file);
  };

  const removePhoto = () => { setForm(prev => ({ ...prev, insurance_card_photo: '' })); setPhotoPreview(null); setPhotoName(''); };

  const togglePriority = (option) => {
    setForm(prev => {
      const current = prev.selected_priorities;
      const updated = current.includes(option) ? current.filter(p => p !== option) : current.length < 5 ? [...current, option] : current;
      // Clear rankings if a ranked item was deselected
      const r1 = updated.includes(prev.priority_rank_1) ? prev.priority_rank_1 : '';
      const r2 = updated.includes(prev.priority_rank_2) ? prev.priority_rank_2 : '';
      const r3 = updated.includes(prev.priority_rank_3) ? prev.priority_rank_3 : '';
      return { ...prev, selected_priorities: updated, priority_rank_1: r1, priority_rank_2: r2, priority_rank_3: r3 };
    });
  };

  const handleChangeRank = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const handleChangeMustKeep = (value) => setForm(prev => ({ ...prev, other_concerns: value }));

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/intake`, form);
      setIsSubmitted(true);
      sonnerToast.success('Form submitted successfully');
    } catch {
      sonnerToast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center py-16">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            Your information has been received securely. A TruBridge Health advisor will review your details and reach out to you shortly.
          </p>
          <a href="/"><Button className="bg-blue-600 hover:bg-blue-700 text-white">Return to Homepage</Button></a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="TruBridge Health" className="h-12 w-auto mx-auto mb-4" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Medicare Information Intake</h1>
          <p className="text-gray-500 text-sm sm:text-base">
            Please provide the following information so we can better assist you with your Medicare options.
          </p>
        </div>

        {/* Security Notice */}
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6" data-testid="security-notice">
          <Lock className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-800">
            Your information is transmitted securely over an encrypted connection. We take your privacy seriously and will never share your data without your consent.
          </p>
        </div>

        <ProgressBar step={step} />

        {step === 0 && (
          <StepIntake
            form={form} onChange={handleChange}
            onPhotoUpload={handlePhotoUpload} photoPreview={photoPreview} photoName={photoName} removePhoto={removePhoto}
            onContinue={() => { window.scrollTo(0, 0); setStep(1); }}
          />
        )}
        {step === 1 && (
          <StepPriorities
            selected={form.selected_priorities}
            onToggle={togglePriority}
            onBack={() => { window.scrollTo(0, 0); setStep(0); }}
            onContinue={() => { window.scrollTo(0, 0); setStep(2); }}
          />
        )}
        {step === 2 && (
          <StepRanking
            selected={form.selected_priorities}
            rank1={form.priority_rank_1} rank2={form.priority_rank_2} rank3={form.priority_rank_3}
            mustKeep={form.other_concerns}
            onChangeRank={handleChangeRank} onChangeMustKeep={handleChangeMustKeep}
            onBack={() => { window.scrollTo(0, 0); setStep(1); }}
            onSubmit={handleSubmit} isSubmitting={isSubmitting}
          />
        )}
      </div>
    </div>
  );
}
